// CCS-431: Care Notes Reports. Pure helpers (no React, no network) so the
// report's filtering, searching, sorting and export mapping can be unit tested.
//
// Data reality: a care note is only { note, timeCreated } stored on a shift,
// and GET /shifts returns just the latest one (`latestCareNote`). Every other
// report field comes from the shift itself. Fields with no backing data (Note
// Type, Audit Status, Signature, Area, cancelled notes) are deliberately not
// produced here; add them to `shiftToCareNotes`, FILTER/SORT/EXPORT below once
// the API supplies them.
import {
  formatUKTime,
  formatUTCForDB,
  parseDbTimestamp,
  toUKWallTime,
  ukWallTimeToUTC,
} from "./functions.js";
import { DEFAULT_PROVIDER_NAME } from "./providerName.js";

export const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Unassigned", value: "unassigned" },
  { label: "In Progress", value: "inprogress" },
  { label: "Late", value: "late" },
  { label: "Missed", value: "missed" },
  { label: "Completed", value: "completed" },
];

// Shift / Visit Time buckets, by the UK wall-clock hour the shift starts
export const VISIT_PERIOD_OPTIONS = [
  { label: "Morning (06:00–11:59)", value: "morning", from: 6, to: 12 },
  { label: "Afternoon (12:00–16:59)", value: "afternoon", from: 12, to: 17 },
  { label: "Evening (17:00–21:59)", value: "evening", from: 17, to: 22 },
  { label: "Night (22:00–05:59)", value: "night", from: 22, to: 6 },
];

// Care notes hold sensitive service-user information: admins only. This is a
// UI check; the API itself does not enforce roles or tenant isolation.
export const canViewCareNotesReports = (groups = []) =>
  Array.isArray(groups) && groups.includes("admin");

export const EMPTY_FILTERS = {
  dateFrom: "",
  dateTo: "",
  serviceUsers: [],
  staff: [],
  statuses: [],
  visitPeriod: null,
  keyword: "",
};

export const DEFAULT_SORT = { key: "dateTime", direction: "desc" };
export const PAGE_SIZE = 10;
export const SHIFT_FETCH_LIMIT = 1000;
export const NOTE_SUMMARY_LENGTH = 120;

// Caveats printed on screen and in every print/PDF/Excel header
export const LATEST_NOTE_NOTICE =
  "Shows the most recent care note recorded on each shift; earlier notes on the same shift are not included.";
// GET /shifts returns newest shifts first, so a capped result drops the oldest
export const truncatedNotice = (limit = SHIFT_FETCH_LIMIT) =>
  `May be incomplete: only the most recent ${limit} shifts were searched. Narrow the filters (e.g. the date range) for a complete report.`;

const DATE_TIME_FORMAT = "dd MMM yyyy HH:mm";

const fullName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ");

const capitalize = (text) =>
  text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";

const statusLabel = (status) => {
  const option = STATUS_OPTIONS.find(
    (o) => o.value === String(status || "").toLowerCase()
  );
  return option ? option.label : capitalize(status);
};

const timestamp = (value) => parseDbTimestamp(value)?.getTime() ?? null;

// The notes recorded on a shift. Prefer the full history when the API sends
// it (`careNotes`), otherwise the latest note GET /shifts returns.
const shiftNotes = (shift) => {
  if (Array.isArray(shift?.careNotes) && shift.careNotes.length > 0) {
    return shift.careNotes;
  }
  return Array.isArray(shift?.latestCareNote) ? shift.latestCareNote : [];
};

// One report row per non-empty care note on a shift
export const shiftToCareNotes = (shift) =>
  shiftNotes(shift)
    .map((careNote, index) => ({ careNote, index }))
    .filter(({ careNote }) => String(careNote?.note ?? "").trim() !== "")
    .map(({ careNote, index }) => ({
      id: `${shift.shiftId}-${index}`,
      rotaRef: String(shift.shiftId ?? ""),
      shiftId: shift.shiftId,
      // A note without its own timestamp is dated by its shift
      dateTime: careNote.timeCreated || shift.shiftStart || null,
      serviceUserId: shift.serviceUser?.id ?? null,
      serviceUser: fullName(shift.serviceUser),
      staffId: shift.staff?.id ?? null,
      staffMember: fullName(shift.staff),
      careType: [capitalize(shift.call), shift.shiftType]
        .filter(Boolean)
        .join(" – "),
      status: String(shift.status || "").toLowerCase(),
      shiftStart: shift.shiftStart || null,
      shiftEnd: shift.shiftEnd || null,
      note: String(careNote.note).trim(),
    }));

export const shiftsToCareNotes = (shifts = []) =>
  (Array.isArray(shifts) ? shifts : []).flatMap(shiftToCareNotes);

// The date pickers have no time selection, so "Date To" arrives as midnight
// (UK) of the chosen day. Treat a midnight bound as the whole day so a
// same-day range and the last day of a range are included.
export const inclusiveDateTo = (dateTo) => {
  const wall = toUKWallTime(dateTo);
  if (!wall) return dateTo;
  if (wall.getHours() || wall.getMinutes() || wall.getSeconds()) return dateTo;
  const endOfDay = new Date(wall);
  endOfDay.setHours(23, 59, 59, 0);
  return formatUTCForDB(ukWallTimeToUTC(endOfDay), "yyyy-MM-dd HH:mm:ss");
};

// Plain-text truncation (note text is never parsed as HTML)
export const summariseNote = (note, length = NOTE_SUMMARY_LENGTH) => {
  // Array.from splits by code point, so emoji are never cut in half
  const chars = Array.from(String(note ?? ""));
  return chars.length > length
    ? `${chars.slice(0, length).join("").trimEnd()}…`
    : chars.join("");
};

export const formatNoteDateTime = (row) =>
  formatUKTime(row.dateTime, DATE_TIME_FORMAT);

export const formatVisitTime = (row) => {
  if (!row.shiftStart) return "--";
  const start = formatUKTime(row.shiftStart, "HH:mm");
  return row.shiftEnd
    ? `${start}–${formatUKTime(row.shiftEnd, "HH:mm")}`
    : start;
};

export const formatStatus = (row) => statusLabel(row.status);

export const getVisitPeriod = (shiftStart) => {
  const wall = toUKWallTime(shiftStart);
  if (!wall) return null;
  const hour = wall.getHours();
  const period = VISIT_PERIOD_OPTIONS.find(({ from, to }) =>
    from < to ? hour >= from && hour < to : hour >= from || hour < to
  );
  return period ? period.value : null;
};

const optionValues = (options) =>
  (options || []).map((o) => String(o?.value ?? o));

// Checks the filters before a report can be generated
export const validateReportFilters = (filters) => {
  const from = timestamp(filters.dateFrom);
  const to = timestamp(filters.dateTo);
  if (from !== null && to !== null && from > to) {
    return "Date From must be before Date To";
  }
  return null;
};

// GET /shifts only returns shifts that lie wholly inside shiftStart..shiftEnd,
// but the report includes every visit that STARTS in range, e.g. a 30 Sep
// 22:00 - 1 Oct 07:00 shift belongs to September. Widen the shift window by
// this much and let filterCareNotes apply the exact visit-start range.
export const SHIFT_WINDOW_PADDING_MS = 24 * 60 * 60 * 1000;

const shiftBound = (value, offsetMs) => {
  const date = parseDbTimestamp(value);
  return date
    ? formatUTCForDB(new Date(date.getTime() + offsetMs), "yyyy-MM-dd HH:mm:ss")
    : value;
};

// Narrow the GET /shifts call where the API supports it (one id per filter);
// everything is re-checked client side by filterCareNotes.
export const buildShiftQuery = (filters) => {
  const serviceUserIds = optionValues(filters.serviceUsers);
  const staffIds = optionValues(filters.staff);
  const query = { limit: SHIFT_FETCH_LIMIT };
  if (serviceUserIds.length === 1) query.serviceUserId = serviceUserIds[0];
  if (staffIds.length === 1) query.staffId = staffIds[0];
  if (filters.dateFrom) {
    query.shiftStart = shiftBound(filters.dateFrom, -SHIFT_WINDOW_PADDING_MS);
  }
  if (filters.dateTo) {
    query.shiftEnd = shiftBound(filters.dateTo, SHIFT_WINDOW_PADDING_MS);
  }
  return query;
};

export const filterCareNotes = (rows, filters = EMPTY_FILTERS) => {
  const from = timestamp(filters.dateFrom);
  const to = timestamp(filters.dateTo);
  const serviceUserIds = optionValues(filters.serviceUsers);
  const staffIds = optionValues(filters.staff);
  const statuses = optionValues(filters.statuses);
  const visitPeriod = filters.visitPeriod?.value ?? filters.visitPeriod ?? "";
  const keyword = String(filters.keyword || "").trim().toLowerCase();

  return rows.filter((row) => {
    // The date range is by visit (shift start), like the ROTA page: a note
    // belongs to the visit it was written for, even if it was saved later.
    // Notes are stamped when saved, so filtering by note time would drop a
    // September visit whose note was entered in October.
    const at = timestamp(row.shiftStart ?? row.dateTime);
    if (from !== null && (at === null || at < from)) return false;
    // Bounds are whole seconds; stored times can carry fractions of a second
    if (to !== null && (at === null || at >= to + 1000)) return false;
    if (
      serviceUserIds.length &&
      !serviceUserIds.includes(String(row.serviceUserId))
    ) {
      return false;
    }
    if (staffIds.length && !staffIds.includes(String(row.staffId))) {
      return false;
    }
    if (statuses.length && !statuses.includes(row.status)) return false;
    if (visitPeriod && getVisitPeriod(row.shiftStart) !== visitPeriod) {
      return false;
    }
    if (keyword && !row.note.toLowerCase().includes(keyword)) return false;
    return true;
  });
};

// "Search results..." — searches only the already generated rows, across
// every displayed column
export const searchCareNotes = (rows, term) => {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    [
      formatNoteDateTime(row),
      row.rotaRef,
      row.serviceUser,
      row.staffMember,
      row.careType,
      formatVisitTime(row),
      formatStatus(row),
      row.note,
    ].some((value) => String(value).toLowerCase().includes(needle))
  );
};

export const SORTABLE_COLUMNS = {
  dateTime: (row) => timestamp(row.dateTime) ?? -Infinity,
  rotaRef: (row) => row.rotaRef,
  serviceUser: (row) => row.serviceUser.toLowerCase(),
  staffMember: (row) => row.staffMember.toLowerCase(),
  careType: (row) => row.careType.toLowerCase(),
  status: (row) => formatStatus(row).toLowerCase(),
};

export const sortCareNotes = (rows, sort = DEFAULT_SORT) => {
  const accessor = SORTABLE_COLUMNS[sort?.key] ?? SORTABLE_COLUMNS.dateTime;
  const factor = sort?.direction === "asc" ? 1 : -1;
  // Ties fall back to newest first so the order is always deterministic
  const byDateDesc = (a, b) =>
    SORTABLE_COLUMNS.dateTime(b) - SORTABLE_COLUMNS.dateTime(a);
  return [...rows].sort((a, b) => {
    const x = accessor(a);
    const y = accessor(b);
    const order =
      typeof x === "string" && typeof y === "string"
        ? x.localeCompare(y, "en-GB", { numeric: true })
        : x < y
        ? -1
        : x > y
        ? 1
        : 0;
    return order ? order * factor : byDateDesc(a, b);
  });
};

// Clicking a header: new column starts newest/A-Z first, same column flips
export const nextSort = (current, key) => {
  if (current?.key !== key) {
    return { key, direction: key === "dateTime" ? "desc" : "asc" };
  }
  return { key, direction: current.direction === "asc" ? "desc" : "asc" };
};

// Print/export apply to the selected rows that are currently shown, otherwise
// to everything currently shown (generated report narrowed by "Search
// results..."). Rows hidden by the search are never exported.
export const getSelectedShownRows = (shownRows, selectedIds) =>
  selectedIds && selectedIds.size > 0
    ? shownRows.filter((row) => selectedIds.has(row.id))
    : [];

export const getOutputRows = (shownRows, selectedIds) => {
  const selected = getSelectedShownRows(shownRows, selectedIds);
  return selected.length ? selected : shownRows;
};

// Keep pageIndex valid when the shown rows shrink (search, regenerate)
export const clampPageIndex = (pageIndex, total, pageSize = PAGE_SIZE) =>
  Math.min(Math.max(pageIndex, 0), Math.max(Math.ceil(total / pageSize) - 1, 0));

export const getPageSummary = (total, pageIndex, pageSize = PAGE_SIZE) => {
  if (!total) return "Showing 0 of 0 care notes";
  const from = clampPageIndex(pageIndex, total, pageSize) * pageSize + 1;
  const to = Math.min(total, from - 1 + pageSize);
  return `Showing ${from}–${to} of ${total} care note${total === 1 ? "" : "s"}`;
};

export const EXPORT_COLUMNS = [
  { header: "Date & Time", value: formatNoteDateTime, width: 18 },
  { header: "Rota Ref", value: (row) => row.rotaRef, width: 10 },
  { header: "Service User", value: (row) => row.serviceUser, width: 22 },
  { header: "Staff Member", value: (row) => row.staffMember, width: 22 },
  { header: "Care Type", value: (row) => row.careType, width: 20 },
  { header: "Shift / Visit Time", value: formatVisitTime, width: 16 },
  { header: "Status", value: formatStatus, width: 12 },
  { header: "Note", value: (row) => row.note, width: 60 },
];

export const toExportRows = (rows) =>
  rows.map((row) => EXPORT_COLUMNS.map((column) => column.value(row)));

const labels = (options) => (options || []).map((o) => o.label).join(", ");

// Human-readable criteria for the print/PDF/Excel header
// "Care Notes Report", or with the one service user and/or staff member the
// report is about, e.g. "Care Notes Report – Mary Johnson"
export const reportTitle = (filters) => {
  const subjects = [filters.serviceUsers, filters.staff]
    .filter((options) => options?.length === 1)
    .map((options) => options[0].label);
  return subjects.length
    ? `Care Notes Report – ${subjects.join(" · ")}`
    : "Care Notes Report";
};

export const describeReport = ({
  filters,
  search,
  generatedAt,
  selectedCount = 0,
  truncated = false,
  providerName = DEFAULT_PROVIDER_NAME,
}) => {
  const dateRange =
    filters.dateFrom || filters.dateTo
      ? `${
          filters.dateFrom ? formatUKTime(filters.dateFrom, DATE_TIME_FORMAT) : "Any"
        } to ${
          filters.dateTo ? formatUKTime(filters.dateTo, DATE_TIME_FORMAT) : "Any"
        }`
      : "All dates";

  const appliedFilters = [
    ["Service User", labels(filters.serviceUsers)],
    ["Staff Member", labels(filters.staff)],
    ["Status", labels(filters.statuses)],
    ["Shift / Visit Time", filters.visitPeriod?.label ?? ""],
    ["Contains Text", String(filters.keyword || "").trim()],
    ["Search Within Results", String(search || "").trim()],
    ["Records", selectedCount ? `${selectedCount} selected` : ""],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => ({ label, value }));

  return {
    branding: "Care Connect Software",
    providerName,
    title: reportTitle(filters),
    dateRange,
    appliedFilters,
    generatedAt: formatUKTime(generatedAt, "dd MMM yyyy HH:mm:ss"),
    notices: [
      LATEST_NOTE_NOTICE,
      ...(truncated ? [truncatedNotice()] : []),
    ],
  };
};

export const reportFileName = (generatedAt, extension) =>
  `care-notes-${formatUKTime(generatedAt, "yyyyMMdd-HHmm")}.${extension}`;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Self-contained printable document (opened in a new window and printed)
export const buildPrintHtml = (meta, rows) => {
  const filterItems = meta.appliedFilters.length
    ? meta.appliedFilters
        .map(
          (f) =>
            `<li><strong>${escapeHtml(f.label)}:</strong> ${escapeHtml(f.value)}</li>`
        )
        .join("")
    : "<li>None</li>";
  const head = EXPORT_COLUMNS.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");
  const body = rows.length
    ? toExportRows(rows)
        .map(
          (cells) =>
            `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`
        )
        .join("")
    : `<tr><td colspan="${EXPORT_COLUMNS.length}">No care notes found</td></tr>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(meta.title)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm 12mm 16mm;
    @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 9px sans-serif; } }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  .brand { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #555; }
  h1 { font-size: 18px; margin: 4px 0 8px; }
  .meta { margin: 0 0 4px; }
  ul { margin: 4px 0 12px; padding-left: 16px; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #eee; }
</style>
</head>
<body>
<div class="brand">${escapeHtml(meta.branding)}</div>
<p class="meta"><strong>Provider:</strong> ${escapeHtml(meta.providerName)}</p>
<h1>${escapeHtml(meta.title)}</h1>
<p class="meta"><strong>Visit dates:</strong> ${escapeHtml(meta.dateRange)}</p>
<p class="meta"><strong>Generated:</strong> ${escapeHtml(meta.generatedAt)}</p>
<p class="meta"><strong>Care notes:</strong> ${rows.length}</p>
${meta.notices
  .map((n) => `<p class="meta notice"><strong>Note:</strong> ${escapeHtml(n)}</p>`)
  .join("")}
<p class="meta"><strong>Applied filters:</strong></p>
<ul>${filterItems}</ul>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body>
</html>`;
};
